import { Copy, ExternalLink, PowerOff } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation, buildStoreUrl } from '@dukanti/shared';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { Button } from '@/components/ui/button';
import { StoreStatusBadge } from './store-status-badge';

export interface PublishedStoreCardProps {
  /** Slug of the published store. `buildStoreUrl(slug)` generates the
   *  public URL that both the preview text and the "View your store"
   *  affordance share — the single source of truth for the URL. */
  readonly storeSlug: string;
  /** Fires when the merchant clicks the ghost "Unpublish" button. The
   *  parent opens `UnpublishStoreDialog` from its controlled state. */
  readonly onUnpublishClick: () => void;
}

/**
 * Live-state dashboard card shown once the store is published (AC5, AC6).
 *
 * Layout responds to width:
 *   - Mobile (< sm): vertical stack (`flex-col`) — the two action
 *     buttons sit on top of each other so each has a full-width touch
 *     target. The "Unpublish" ghost sits below.
 *   - ≥ sm: horizontal action row — "Copy" and "View" sit side by side.
 *
 * URL rendering:
 *   - The preview string is always wrapped in `<span dir="ltr">` so the
 *     `http://…` prefix renders LTR inside an Arabic parent (clarifier
 *     Pitfall: without the explicit `dir`, RTL would flip the `://` and
 *     the slash order, producing a visually broken URL).
 *   - `text-ellipsis overflow-hidden` truncates long slugs on narrow
 *     viewports.
 *
 * Copy flow (`useCopyToClipboard`):
 *   - `copy()` never throws; it resolves `true` on success, `false` on
 *     failure. We branch on the boolean: success fires the translated
 *     `toasts.urlCopied` sonner toast; failure fires the translated
 *     `errors.copyFailed` toast.
 *   - The button disables during `isCopying` so repeat clicks don't
 *     race (though the underlying hook is already idempotent).
 *
 * View flow:
 *   - `window.open(url, '_blank', 'noopener,noreferrer')` rather than a
 *     plain `<a target="_blank">` because we need the `noopener` guard
 *     to prevent the opened storefront from calling `window.opener`
 *     back into the admin context (reverse tabnabbing). The equivalent
 *     anchor-based form is also safe in modern browsers but we keep the
 *     explicit `window.open` here so the security property is visible
 *     at the call site.
 *
 * Unpublish is a neutral ghost button (per F-004 clarifier Decision 2 —
 * NOT destructive red) because unpublishing is fully reversible and
 * recoloring it red would imply data loss.
 *
 * Test-ids (F-004 design §5.5):
 *   - `published-store-card`              — root card
 *   - `published-store-card-actions`      — action-row container that
 *     asserts `flex-col` on mobile (W2 test refinement)
 *   - `published-store-url`               — the URL preview text
 *   - `published-store-copy`              — Copy URL button
 *   - `published-store-view`              — View your store button
 *   - `published-store-unpublish`         — Unpublish ghost button
 */
export function PublishedStoreCard({ storeSlug, onUnpublishClick }: PublishedStoreCardProps) {
  const { t, dir } = useTranslation();
  const { copy, isCopying } = useCopyToClipboard();

  const publicUrl = buildStoreUrl(storeSlug);

  async function handleCopyClick(): Promise<void> {
    const ok = await copy(publicUrl);
    if (ok) {
      toast.success(t('admin.publishStore.toasts.urlCopied'));
    } else {
      // `copyFailed` is an error-side translation key. The `useCopyToClipboard`
      // hook already attempted both the async Clipboard API and the legacy
      // execCommand fallback; if both failed we surface the actionable
      // "Select and copy manually" copy so the merchant can still recover.
      toast.error(t('admin.publishStore.errors.copyFailed'));
    }
  }

  function handleViewClick(): void {
    // Open in a new tab with the reverse-tabnabbing guard. The `noopener`
    // in the features string nulls `window.opener` in the new context
    // so the storefront tab cannot navigate the admin tab via
    // `window.opener.location`. `noreferrer` additionally omits the
    // Referer header.
    window.open(publicUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div
      dir={dir}
      data-testid="published-store-card"
      className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border"
    >
      {/* Header: title + status badge on the same row, URL beneath. */}
      <div className="border-border border-b px-5 py-4">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-foreground text-start text-base font-semibold">
            {t('admin.publishStore.publishedCard.title')}
          </h2>
          <StoreStatusBadge
            status="published"
            className="ms-auto"
            testIdPrefix="published-store-card-status-badge"
          />
        </div>

        {/* URL label — quiet caption above the URL preview. */}
        <p className="text-muted-foreground mt-2 text-start text-xs font-medium uppercase tracking-wider">
          {t('admin.publishStore.publishedCard.urlLabel')}
        </p>

        {/* URL text — forced LTR so the http:// prefix and slashes
            render correctly inside an Arabic parent. Truncated on
            narrow viewports so long slugs don't force horizontal
            scrolling of the card. */}
        <p
          dir="ltr"
          data-testid="published-store-url"
          className="text-foreground mt-1 truncate text-start text-sm font-medium"
        >
          {publicUrl}
        </p>
      </div>

      {/* Action row — stacks on mobile, flows horizontally on ≥sm. The
          `data-testid="published-store-card-actions"` tag lets the test
          suite assert `flex-col` at mobile widths (W2). */}
      <div
        data-testid="published-store-card-actions"
        className="flex flex-col gap-2 px-5 py-4 sm:flex-row"
      >
        <Button
          type="button"
          variant="outline"
          onClick={handleCopyClick}
          disabled={isCopying}
          aria-label={t('admin.publishStore.publishedCard.copyAriaLabel')}
          data-testid="published-store-copy"
          className="border-border text-foreground hover:bg-admin-surface-muted w-full sm:flex-1"
        >
          <Copy aria-hidden="true" className="size-4" strokeWidth={1.75} />
          <span>{t('admin.publishStore.publishedCard.copy')}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleViewClick}
          data-testid="published-store-view"
          className="border-border text-foreground hover:bg-admin-surface-muted w-full sm:flex-1"
        >
          <ExternalLink aria-hidden="true" className="size-4 rtl:rotate-180" strokeWidth={1.75} />
          <span>{t('admin.publishStore.publishedCard.viewStore')}</span>
        </Button>
      </div>

      {/* Unpublish — neutral ghost button with a thin border so it
          reads as a secondary action, not a destructive one. NOT
          variant="destructive" (Decision 2). */}
      <div className="px-5 pb-4">
        <Button
          type="button"
          variant="ghost"
          onClick={onUnpublishClick}
          data-testid="published-store-unpublish"
          className="border-border text-muted-foreground hover:bg-admin-surface-muted hover:text-foreground w-full border"
        >
          <PowerOff aria-hidden="true" className="size-4" strokeWidth={1.75} />
          <span>{t('admin.publishStore.unpublishCtaLabel')}</span>
        </Button>
      </div>
    </div>
  );
}
